import React, { useState } from "react";
import { useForm } from "react-hook-form";
import image1 from "@assets/digital-illustration-section_3_1.svg";
import { emailService } from "@services/EmailService";
import Loader from "@shared/components/Loader";
import InlineAlert from "@shared/components/InlineAlert";
import Footer from "./Footer";

type FormData = {
  name: string;
  email: string;
  subject: string;
  message: string;
};

const GetInTouch: React.FC = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>();

  const [isLoading, setIsLoading] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const onSubmit = async (data: FormData) => {
    try {
      setIsLoading(true);
      await emailService.sendContactEmail(data);
      setMessageSent(true);
      reset();
    } catch (error) {
      setAlert({
        type: "error",
        message: "Oops! Something went wrong. Please try again later.",
      });
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="h-[calc(100vh-75px)] 2xl:h-[calc(100vh-96px)] md:pt-12 pt-6 flex flex-col  bg-[#fff] snap-start transition-all duration-700">
      {/* <div className="flex flex-col h-[calc(100vh-96px)]"> */}
      <div className="flex-1 px-8 md:px-24">
        <div className="flex items-center justify-between flex-col md:flex-row space-y-6 md:space-y-0">
          <div className="flex items-center justify-center w-[70%] md:w-1/2 h-full">
            <img
              src={image1}
              alt="Contact illustration"
              className="md:w-[65%] w-full"
            />
          </div>
          <div className="w-full md:w-[45%] h-full flex flex-col justify-center">
            {alert && (
              <InlineAlert
                type={alert.type}
                message={alert.message}
                onClose={() => setAlert(null)}
              />
            )}

            <div className="pt-3 text-center md:text-left mt-[-15px] md:mt-0 pb-8">
              {messageSent ? (
                <>
                  <p className="text-2xl font-semibold mb-2">Message sent.</p>
                  <p>Thanks for reaching out. We'll be in touch shortly.</p>
                </>
              ) : (
                <>
                  <h2 className="text-lg md:text-2xl font-semibold mb-2 md:mb-4 text-gray-800">
                    Get in touch
                  </h2>
                  <p className="text-gray-600 sm:mb-6 2xl:mb-12 text-sm md:text-base mb-2">
                    Get in touch with our team to see THE ARK platform in
                    action.
                  </p>
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="space-y-4 pb-4"
                  >
                    <div>
                      <input
                        type="text"
                        placeholder="Name"
                        className="w-full px-3 md:px-4 py-2 md:py-3 rounded bg-[#F2F4F7] text-sm md:text-base"
                        {...register("name", { required: "Name is required" })}
                      />
                      {errors.name && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.name.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <input
                        type="email"
                        placeholder="Email"
                        className="w-full px-3 md:px-4 py-2 md:py-3 rounded bg-[#F2F4F7] text-sm md:text-base"
                        {...register("email", {
                          required: "Email is required",
                          pattern: {
                            value:
                              /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                            message: "Enter a valid email address",
                          },
                        })}
                      />
                      {errors.email && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.email.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <input
                        type="text"
                        placeholder="Subject"
                        className="w-full px-3 md:px-4 py-2 md:py-3 rounded bg-[#F2F4F7] text-sm md:text-base"
                        {...register("subject", {
                          required: "Subject is required",
                        })}
                      />
                      {errors.subject && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.subject.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <textarea
                        placeholder="Your message..."
                        className="w-full px-3 md:px-4 py-2 md:py-3 rounded bg-[#F2F4F7] h-32 text-sm md:text-base"
                        {...register("message", {
                          required: "Message is required",
                        })}
                      />
                      {errors.message && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.message.message}
                        </p>
                      )}
                    </div>

                    <button
                      type="submit"
                      className="bg-blue-600 text-white text-sm md:text-base float-end px-6 py-2 rounded hover:bg-blue-700 transition w-full sm:w-auto"
                      disabled={isLoading}
                    >
                      {!isLoading ? <span>Send</span> : <Loader color="#fff" />}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
      {/* </div> */}
    </section>
  );
};

export default GetInTouch;
